import cx from 'clsx';
import { addMinutes, format } from 'date-fns';
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as AvatarIcon } from '@assets/svg/avatar.svg';
import { ReactComponent as EditCommentIcon } from '@assets/svg/ic-edit-comment.svg';
import { ReactComponent as ArrowIcon } from '@assets/svg/lightArrowDown.svg';

import FilePreviewIcon from '@components/common/FilePreviewIcon/FilePreviewIcon';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { getUrlResizeImage } from '@helpers/getUrlResizeImage';

import useTranslation from '@hooks/useTranslation';

import type { IComment } from '@model/actionItem';

import TranslateBtn from '@pages/ActionItem/components/TranslateBtn/TranslateBtn';
import useLiveTranslate from '@pages/ActionItem/hooks/useLiveTranslate';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import style from './style.module.scss';

interface IProps {
  commentItem: IComment;
  aiType: string;
  userTimezone: number;
  tooltipId: string;
}

const AVAILABLE_SYMBOLS = 500;

const imagesExtensions = { jpg: true, jpeg: true, png: true };

const availableSources = {
  'customer-dashboard': true,
  'customer-dashboard-app': true,
  'self-service': true,
  'self-service-app': true,
};

const UserMessage: React.FC<IProps> = ({ commentItem, aiType, userTimezone, tooltipId }) => {
  const { t } = useTranslation();

  const [showMore, setShowMore] = useState(false);

  const { data: profileData } = useUserProfileQuery();

  const { setActiveModal } = useModalActions();
  const {
    user_name,
    time_created,
    comment,
    attachments,
    id,
    text_updated_at,
    time_delete,
    source,
    user_pic,
  } = commentItem;

  const dateTimeFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;
  const dateTimeDelete = format(addMinutes(time_created, userTimezone), dateTimeFormat);

  const userImage =
    user_pic && !user_pic?.includes('placeholder') ? user_pic : profileData?.customer.comment_image;

  const { hasTranslate, translates, isTranslated, isTranslateLoading, toggleIsTranslate } = useLiveTranslate({
    item: commentItem,
    fields: ['comment'],
    entityName: 'comment',
    entityId: id,
  });

  const openLightbox = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target;

    if (!(target instanceof HTMLImageElement)) return;

    const attachImages = attachments.map((item) => ({ src: item.url }));
    const commentImages =
      comment
        .match(/<img [^>]*src="[^"]*"[^>]*>/gm)
        ?.map((x) => ({ src: x.replace(/.*src="([^"]*)".*/, '$1') })) || [];

    const images = [...attachImages, ...commentImages];

    if (images.length) {
      const index = images.findIndex((image) => image.src === target?.currentSrc);

      setActiveModal({ name: MODAL_NAMES.lightboxModal, options: { images, index } });
    }
  };

  const handleEditComment = () => {
    setActiveModal({
      name: MODAL_NAMES.editCommentModal,
      options: { comment: commentItem, aiType },
    });
  };

  const commentText = (translates['comment'] || '').replace(/\sstyle="[^"]*"/g, '');
  const isLargeComment = commentText.replace(/(<([^>]+)>)/gi, '').length > AVAILABLE_SYMBOLS;
  const currentComment =
    isLargeComment && !showMore ? commentText.substring(0, AVAILABLE_SYMBOLS) + '...' : commentText;

  return (
    <div className={style.comment}>
      {availableSources[source as keyof typeof availableSources] && commentItem.type == 1 ? (
        <img className={style.commentUserAvatar} src={userImage} alt="" />
      ) : (
        <AvatarIcon className={style.commentUserAvatar} />
      )}
      {time_delete ? (
        <div className={style.commentBody}>
          {t(`This comment was deleted. by ${user_name} at ${dateTimeDelete}`)}
        </div>
      ) : (
        <>
          <div className={style.commentHead}>
            <div className={style.commentInfo}>
              <span className={style.commentAuthor}>{user_name}:</span>
              <span className={style.commentDate}>
                {format(addMinutes(time_created, userTimezone), dateTimeFormat)}
              </span>
              {text_updated_at && <span className={style.commentEdited}>{t('Edited')}</span>}
            </div>
            <div className={style.commentActions}>
              <TranslateBtn
                isTranslated={isTranslated}
                hasTranslate={hasTranslate}
                isTranslateLoading={isTranslateLoading}
                toggleIsTranslate={toggleIsTranslate}
              />
              {commentItem.user_name === profileData?.user.full_name && (
                <EditCommentIcon
                  className={style.editCommentIcon}
                  data-tooltip-id={tooltipId}
                  data-tooltip-content={t('Edit comment')}
                  onClick={handleEditComment}
                />
              )}
            </div>
          </div>
          <div className={style.commentBody} data-qa={'comment-body'}>
            <div
              onClick={(e) => openLightbox(e)}
              className={style.commentMessage}
              dangerouslySetInnerHTML={{ __html: currentComment }}
            />
            {isLargeComment && (
              <div className={style.extraBlock}>
                <span onClick={() => setShowMore(!showMore)}>
                  {showMore ? t('Show less') : t('Show more')}
                  <ArrowIcon className={cx(style.arrowIcon, { [style.arrowTop]: showMore })} />
                </span>
              </div>
            )}
            {!!attachments.length && (
              <div className={style.commentBodyFiles}>
                {attachments.map((file) =>
                  imagesExtensions[file.extension as keyof typeof imagesExtensions] ? (
                    <div className={style.commentMessage} onClick={(e) => openLightbox(e)} key={uuidv4()}>
                      <img
                        className={style.imageItem}
                        src={getUrlResizeImage(file.url, '300_400')}
                        alt={file.name}
                      />
                    </div>
                  ) : (
                    <a
                      key={file.name}
                      href={file.url}
                      target="_blank"
                      className={style.fileItem}
                      download={file.extension !== 'pdf' ? file.name : false}
                    >
                      <FilePreviewIcon file={file} />
                      <span className={style.fileItemName}>{file.name}</span>
                    </a>
                  ),
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserMessage;
